import React, { useEffect, useRef, useState } from 'react';
import Signup from './Signup';
import engagebayBaseAPI from '../api';
import { submitActivate } from '../commons/util/activateFlow';
import loadTestMonials from '../TestMonial-loader';

interface SignupAppProps {
    deps: { [key: string]: any };
}

/**
 * SignupApp orchestrates the signup form state and submission.
 *
 * Why this exists: the old signup/index.tsx wired jQuery event handlers onto
 * ReactDOMServer.renderToString output — no React state, no React events.
 * This component replaces all jQuery logic with React hooks and proper state.
 *
 * Caller: signup/index.tsx bootstrapper.
 * Renders: <Signup ...> with all event handlers and state passed as props.
 */
const SignupApp: React.FC<SignupAppProps> = ({ deps }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(deps.error || null);
    const [showPassword, setShowPassword] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    // Hydrate testimonials skeleton with current testimonial data after mount — same as Login.tsx.
    useEffect(() => {
        if (!deps?.prefs?.isClient) loadTestMonials();
    }, []);

    // Timezone: set timeZoneId hidden input to the client's UTC offset on mount.
    useEffect(() => {
        const tzInput = formRef.current?.elements.namedItem('timeZoneId') as HTMLInputElement | null;
        if (tzInput) {
            tzInput.value = String(-(new Date().getTimezoneOffset()));
        }
    }, []);

    // Referral ID: resolve from deps → localStorage → cookie, write to hidden inputs.
    useEffect(() => {
        let referralUserId: string | null = deps.referralUserId || null;
        if (referralUserId) {
            localStorage.setItem('engagebay_referral_user_id', referralUserId);
        } else {
            referralUserId = localStorage.getItem('engagebay_referral_user_id');
        }
        if (!referralUserId) {
            referralUserId = getCookie('referralUser') || null;
        }
        if (referralUserId && formRef.current) {
            const referrerInput = formRef.current.querySelector<HTMLInputElement>('input[name="referrer_domain_id"]');
            if (referrerInput) referrerInput.value = referralUserId;
            setCookie('referralUser', referralUserId);
        }
        const referrerUrlInput = formRef.current?.querySelector<HTMLInputElement>('input[name="referrer_url"]');
        if (referrerUrlInput) referrerUrlInput.value = document.referrer;
    }, []);

    // Phone: initialise intlTelInput plugin on the phone field after first render.
    useEffect(() => {
        const phoneEl = formRef.current?.querySelector('input.phone-number');
        if (phoneEl) {
            initIntlTelInput(phoneEl, deps.phoneNumber);
        }
    }, []);

    // Fingerprint: collect browser fingerprint; auto-submit when deps.autoSignup.
    useEffect(() => {
        initFingerprintCollection(deps.autoSignup, formRef);
    }, []);

    /**
     * When a valid email is typed, auto-populates the website field with the email domain
     * if the website field is still empty — saves the user one field of manual entry.
     */
    const handleEmailChange = (email: string) => {
        if (!isEmail(email)) return;
        const domain = getWebsiteFromEmail(email);
        if (!domain) return;
        const websiteInput = formRef.current?.querySelector<HTMLInputElement>('input[name="website"]');
        if (websiteInput && !websiteInput.value) {
            websiteInput.value = 'http://' + domain;
        }
    };

    /**
     * Validates phone via intlTelInput, posts form data to signup-user, then
     * delegates to submitActivate to complete the cross-origin cookie exchange.
     *
     * Why URLSearchParams(FormData): encodes all form fields including hidden
     * inputs (timezone, referrer, fingerprint) as application/x-www-form-urlencoded
     * — matching the format the old jQuery $.ajax POST used.
     */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const phoneEl = formRef.current?.querySelector('input.phone-number');
        if (phoneEl && !IntlTelInputUtils.isValid($(phoneEl as HTMLElement))) {
            validatePhoneField(phoneEl as HTMLElement);
            return;
        }

        setLoading(true);
        setError(null);

        const formData = new FormData(e.currentTarget);
        const urlParams = new URLSearchParams(window.location.search);

        try {
            const { data } = await engagebayBaseAPI.post(
                '/rest/api/signup/signup-user',
                new URLSearchParams(formData as any),
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );

            growsumoInit(data);
            ebTrackEmail(data, () => {
                const route = urlParams.get('route') || getCookie('ebroute') || (formData.get('userRoute') as string) || '';
                const coupon = urlParams.get('coupon') || getCookie('ebcoupon') || '';
                submitActivate(data.exchange_token, data.redirect_base, { route, coupon });
            });
        } catch (err: unknown) {
            setLoading(false);
            const axiosErr = err as { response?: { data?: string } };
            setError(axiosErr?.response?.data || 'Signup failed.');
        }
    };

    /**
     * Handles Shopify OAuth sign-in. Extracts the store name and redirects to
     * the OAuth initiation endpoint — same as the old jQuery handler but wired
     * via React's onSubmit so the native form POST never fires.
     */
    const handleShopifySubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const shopName = (new FormData(e.currentTarget).get('shop') as string || '').trim();
        if (shopName) {
            window.location.href = '/oauth?service=shopify_oauth&shopify_store_name=' + encodeURIComponent(shopName);
        }
    };

    return (
        <Signup
            deps={deps}
            loading={loading}
            error={error}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(p => !p)}
            onEmailChange={handleEmailChange}
            onSubmit={handleSubmit}
            onShopifySubmit={handleShopifySubmit}
            formRef={formRef}
        />
    );
};

export default SignupApp;

// ── Helper functions (ported from old index.tsx) ──────────────────────────────

function getCookie(name: string): string | undefined {
    const value = '; ' + document.cookie;
    const parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return undefined;
}

function setCookie(name: string, value: string): void {
    document.cookie = name + '=' + value + ';domain=.engagebay.com;path=/';
}

function isEmail(email: string): boolean {
    const regex = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z]{2,5})+$/;
    return regex.test(email);
}

function getWebsiteFromEmail(email: string): string | undefined {
    try {
        const emailArray = email.split('@');
        return emailArray[1];
    } catch {
        return undefined;
    }
}

function growsumoInit(loginInfo: { name?: string; email?: string }): void {
    try {
        const gs = (window as any).growsumo;
        gs.data.name = loginInfo.name;
        gs.data.email = loginInfo.email;
        gs.data.customer_key = loginInfo.email;
        gs.createSignup();
    } catch {
        // GrowSumo not loaded or signup failed — non-critical, activation proceeds regardless.
    }
}

function ebTrackEmail(data: { email?: string }, callback: () => void): void {
    function runCBK() {
        if (callback) callback();
    }

    if (typeof (window as any).EhAPI !== 'undefined' && (window as any).EhAPI.track) {
        (window as any).EhAPI.track.setEmail(data.email, () => runCBK(), () => runCBK());
    } else if (typeof (window as any).etrk !== 'undefined' && (window as any).etrk.setEmail) {
        (window as any).etrk.setEmail(data.email, true, () => runCBK(), () => runCBK());
    } else {
        runCBK();
    }
}

/**
 * Initialises the intlTelInput jQuery plugin on a phone input element.
 *
 * Why this exists: intlTelInput is a jQuery plugin loaded asynchronously via
 * head.js. Isolating init here lets SignupApp call it from a useEffect without
 * coupling the component to the jQuery/head.js loading details.
 *
 * Precondition: el must be a mounted DOM element — call from a useEffect with
 * an empty dependency array so it runs after the first render.
 */
function initIntlTelInput(el: Element, phoneNumber: string | undefined): void {
    const STATIC_LIB2_PATH = 'https://d2p078bqz5urf7.cloudfront.net/cloud/prod/assets/lib/';
    (window as any).head.js(
        STATIC_LIB2_PATH + '/intl-tel-input-17.0.0/js/intlTelInput.min.js',
        STATIC_LIB2_PATH + '/intl-tel-input-17.0.0/css/intlTelInput.min.css',
        STATIC_LIB2_PATH + '/intl-tel-input-17.0.0/js/utils.js',
        function () {
            if ($(el as HTMLElement).length === 0) return;

            const element = $(el as HTMLElement)[0];
            const iti = (window as any).intlTelInput(element, {
                initialCountry: 'auto',
                nationalMode: false,
                autoHideDialCode: false,
                autoPlaceholder: 'aggressive',
                geoIpLookup: function (callback: (countryCode: string) => void) {
                    try {
                        if (window.location.hostname === 'localhost') {
                            callback('IN');
                            return;
                        }
                        $.get('https://ipinfo.io', function () {}, 'jsonp').always(function (resp: any) {
                            const countryCode = (resp && resp.country) ? resp.country : '';
                            callback(countryCode);
                        });
                    } catch {
                        callback('');
                    }
                },
            });

            if (phoneNumber) {
                const num = String(phoneNumber);
                iti.setNumber(num.indexOf('+') === 0 ? num : '+' + num);
            }
        }
    );

    const phone = $(el as HTMLElement);
    phone[0].addEventListener('keyup', () => {
        validatePhoneField(phone[0]);
    });
}

function validatePhoneField(phone: HTMLElement): void {
    const phoneEle = phone as HTMLInputElement;
    const phoneNumber = (($(phone).val() as string) || '').trim();
    if (!phoneNumber || !IntlTelInputUtils.isValid($(phone))) {
        phoneEle.setCustomValidity('Please enter a valid phone number');
        phoneEle.reportValidity();
    } else {
        phoneEle.setCustomValidity('');
    }
}

/**
 * Utility wrapper around the intlTelInput plugin instance.
 *
 * Why this exists: intlTelInput attaches its instance via window.intlTelInputGlobals,
 * not as a jQuery data attribute. Centralising getInstance() here avoids repeating
 * the lookup across validatePhoneField and handleSubmit.
 *
 * All methods accept a jQuery-wrapped element and return null when the plugin
 * has not yet loaded for that element.
 */
const IntlTelInputUtils = {
    getInstance($el: JQuery<HTMLElement>): any {
        if ($el.length === 0) return null;
        const input = $el[0];
        if ((window as any).intlTelInputGlobals) {
            return (window as any).intlTelInputGlobals.getInstance(input);
        }
        initIntlTelInput($el[0], undefined);
        return null;
    },

    getNumber($el: JQuery<HTMLElement>): string | null {
        const iti = IntlTelInputUtils.getInstance($el);
        return iti ? iti.getNumber() : null;
    },

    setNumber($el: JQuery<HTMLElement>, number: string): void {
        const iti = IntlTelInputUtils.getInstance($el);
        if (iti) {
            const num = String(number);
            iti.setNumber(num.indexOf('+') === 0 ? num : '+' + num);
        } else {
            initIntlTelInput($el[0], number);
        }
    },

    isValid($el: JQuery<HTMLElement>): boolean {
        const iti = IntlTelInputUtils.getInstance($el);
        if (iti) {
            const inputNumber = (($el.val() as string) || '').trim();
            const pluginNumber = (iti.getNumber() || '').trim();
            if (inputNumber && pluginNumber && inputNumber !== pluginNumber) {
                $el.val(pluginNumber);
            }
            return iti.isValidNumber();
        }
        return false;
    },

    getCountryData($el: JQuery<HTMLElement>): any {
        const iti = IntlTelInputUtils.getInstance($el);
        return iti ? iti.getSelectedCountryData() : null;
    },

    setCountry($el: JQuery<HTMLElement>, code: string): any {
        const iti = IntlTelInputUtils.getInstance($el);
        return iti ? iti.setCountry(code) : null;
    },
};

/**
 * Initiates browser fingerprint collection, waiting for the Fingerprint2 library
 * to be available before collecting.
 *
 * Why onload instead of polling: the old approach (5 retries × 500ms) timed out
 * when CloudFront cache was cold after a deployment — ebfp2.1.0.min.js couldn't
 * be fetched within 2.5 seconds for some users. onload waits indefinitely for the
 * browser to finish fetching the library, matching the pattern used in Login.tsx.
 *
 * Priority order:
 * 1. window.Fingerprint2 already set (head script executed before this ran) → collect now.
 * 2. Head script tag exists but not yet loaded → wait for its load event.
 * 3. Library absent entirely → inject from cdnjs (same CDN as Login.tsx) and wait for onload.
 */
function initFingerprintCollection(autoSignup: boolean, ref: React.RefObject<HTMLFormElement>): void {
    if (typeof (window as any).Fingerprint2 !== 'undefined') {
        // Library already executed — browser still needs ~500ms to set up canvas/WebGL for accuracy
        setTimeout(() => collectFingerprint(autoSignup, ref), 500);
        return;
    }
    const onLoaded = () => setTimeout(() => collectFingerprint(autoSignup, ref), 500);
    const existing = document.querySelector<HTMLScriptElement>('script[src*="ebfp2"]');
    if (existing) {
        // Head script is in the DOM but not yet loaded — wait for it (no retry limit)
        existing.addEventListener('load', onLoaded, { once: true });
    } else {
        // Library not in page at all — inject from cdnjs, same as Login.tsx does
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/fingerprintjs2/2.1.4/fingerprint2.min.js';
        script.onload = onLoaded;
        document.head.appendChild(script);
    }
}

/**
 * Runs Fingerprint2.get() and writes the resulting hash to the hidden #fingerprint
 * input. Called only after Fingerprint2 is confirmed available.
 *
 * Why uncontrolled input write (fpInput.value): the fingerprint hidden input uses
 * React's defaultValue so React never resets it on re-render; direct DOM write
 * survives reconciliation while keeping the value out of React state.
 */
function collectFingerprint(autoSignup: boolean, ref: React.RefObject<HTMLFormElement>): void {
    const FP2 = (window as any).Fingerprint2;
    if (!FP2) return;
    const options = {
        excludes: {
            plugins: true,
            localStorage: true,
            adBlock: true,
            screenResolution: true,
            availableScreenResolution: true,
            enumerateDevices: true,
            pixelRatio: true,
            doNotTrack: true,
        },
    };
    FP2.get(options, (components: Array<{ value: unknown }>) => {
        const values = components.map(c => c.value);
        const murmur = FP2.x64hash128(values.join(''), 31);
        const fpInput = ref.current?.querySelector<HTMLInputElement>('#fingerprint');
        if (fpInput) fpInput.value = murmur;
        if (autoSignup && ref.current) {
            ref.current.requestSubmit();
        }
    });
}
